import { isDefined, getAttrValue, isPlainObject, getLocalizedValue, getLocalizedValues, isArray } from '@grandvisionhq/utils';
import { getPriceSpecificationConfig } from '../../utils/get-price-specification-config.js';

const getUrlInfo = (lineItem)=>{
    var ref1;
    const { productType , productSlug , variant: { sku  } ,  } = lineItem;
    const productTypeName = ((ref1 = productType.obj) === null || ref1 === void 0 ? void 0 : ref1.name) || '';
    return {
        productType: productTypeName || '',
        slug: productSlug || '',
        sku: sku || ''
    };
};
const getPaymentMethodUrl = (paymentMethod)=>{
    switch(paymentMethod){
        case 'creditcard':
            return 'http://purl.org/goodrelations/v1#CreditCard';
        case 'paypal':
            return 'http://purl.org/goodrelations/v1#PayPal';
        default:
            return '';
    }
};
const priceVatCalc = (price, rate)=>{
    const priceExVat = price / (1 + rate);
    const vatAmount = priceExVat * rate;
    return {
        priceExVat,
        vatAmount
    };
};
const calcPrice = (value, isCentAmount = true)=>{
    const amount = isCentAmount ? value / 100 : value;
    const price = !isNaN(amount) ? amount.toFixed(2).toString() : '0.00';
    if (price === '0') {
        return '0.00';
    }
    return price;
};
const createOfferObject = (lineItem, quantity)=>{
    var ref3;
    const activeVariant = lineItem.variant;
    var ref2;
    const vat = (ref2 = lineItem === null || lineItem === void 0 ? void 0 : (ref3 = lineItem.taxRate) === null || ref3 === void 0 ? void 0 : ref3.amount) !== null && ref2 !== void 0 ? ref2 : 0;
    var _prices;
    const pricing = ((_prices = activeVariant.prices) !== null && _prices !== void 0 ? _prices : []).find((price)=>typeof price.value.centAmount !== 'undefined'
    );
    const specification = isDefined(pricing) ? getPriceSpecificationConfig(pricing, vat) : undefined;
    return {
        '@type': 'Offer',
        includesObject: {
            '@type': 'TypeAndQuantityNode',
            amountOfThisGood: quantity || 1
        },
        priceSpecification: createPriceSpecification(specification, activeVariant.sku)
    };
};
const convertAttributeToLowerCase = (attribute)=>{
    return Array.isArray(attribute) ? attribute.join().toLowerCase() : attribute.toLowerCase();
};
const createProductObject = (lineItem, quantity, formatProductUrl, selfUrl)=>{
    const urlInfo = getUrlInfo(lineItem);
    let url = selfUrl || window.location && window.location.origin || '';
    if (formatProductUrl && urlInfo) {
        url += formatProductUrl(urlInfo);
    }
    const { variant , productId , name  } = lineItem;
    const modelObj = getAttrValue(variant, 'frameShape');
    const model = isPlainObject(modelObj) ? getLocalizedValue(modelObj, 'en') : '';
    const frameMaterialAttr = getAttrValue(variant, 'frameMaterial');
    const frameMaterial = isPlainObject(frameMaterialAttr) ? getLocalizedValue(frameMaterialAttr, 'en') : '';
    const frameMaterials = Array.isArray(frameMaterialAttr) ? getLocalizedValues(frameMaterialAttr, 'en') : '';
    const frameColorAttr = getAttrValue(variant, 'frameColor');
    const frameColor = isPlainObject(frameColorAttr) ? getLocalizedValue(frameColorAttr, 'en') : '';
    const frameColors = isArray(frameColorAttr) ? getLocalizedValues(frameColorAttr, 'en') : '';
    return {
        '@type': 'Product',
        '@id': `${url}#product`,
        url,
        model,
        name: name.en || '',
        productID: productId || '',
        gtin13: variant.sku || urlInfo.sku || '',
        category: urlInfo.productType || '',
        brand: String(getAttrValue(variant, 'brand')),
        material: convertAttributeToLowerCase(frameMaterials || frameMaterial),
        color: convertAttributeToLowerCase(frameColor || frameColors),
        offers: [
            createOfferObject(lineItem, quantity)
        ]
    };
};
const createPrescriptionGlassesObject = (lineItem, formatProductUrl, selfUrl)=>{
    const { name , productId , brand , variant , productType , slug  } = lineItem.bundleMainLineItem;
    let url = selfUrl || window.location && window.location.origin || '';
    if (formatProductUrl) {
        url += formatProductUrl({
            sku: variant.sku,
            productType: productType,
            slug
        });
    }
    return {
        '@type': 'Product',
        '@id': `${url}#product`,
        url,
        brand,
        name: name.en || '',
        sku: variant.sku || '',
        gtin13: variant.sku || '',
        productID: productId || '',
        category: 'Optical',
        offers: [
            createPrescriptionGlassesOffer(lineItem)
        ]
    };
};
const createPrescriptionGlassesOffer = ({ quantity , price , bundleMainLineItem  })=>({
        '@type': 'Offer',
        includesObject: {
            '@type': 'TypeAndQuantityNode',
            amountOfThisGood: quantity || 1
        },
        priceSpecification: createPrescriptionGlassesPrice(price, bundleMainLineItem.taxRate)
    })
;
const createPrescriptionGlassesPrice = (price, taxRate)=>{
    var ref4;
    var _centAmount;
    const resolvedPrice = (_centAmount = (ref4 = price.discounted) === null || ref4 === void 0 ? void 0 : ref4.value.centAmount) !== null && _centAmount !== void 0 ? _centAmount : price.value.centAmount;
    const resolvedPriceExVat = resolvedPrice * (1 - taxRate.amount);
    const resolvedVatAmount = resolvedPrice * taxRate.amount;
    return {
        '@type': 'CompoundPriceSpecification',
        name: 'Price',
        price: calcPrice(resolvedPrice),
        priceCurrency: price.value.currencyCode,
        valueAddedTaxIncluded: true,
        priceComponent: [
            {
                '@type': 'UnitPriceSpecification',
                name: 'Price excluding VAT',
                price: calcPrice(resolvedPriceExVat),
                valueAddedTaxIncluded: false
            },
            {
                '@type': 'UnitPriceSpecification',
                name: 'VAT',
                price: calcPrice(resolvedVatAmount),
                valueAddedTaxIncluded: true
            }, 
        ]
    };
};
const createOrderObject = (order, paymentMethod, formatProductUrl, selfUrl)=>{
    const { lineItems , prescriptionGlassesLineItems =[] , orderNumber , totalPrice , shippingInfo ,  } = order;
    const originalTotal = [
        ...lineItems,
        ...prescriptionGlassesLineItems
    ].reduce((total, currentValue)=>total += currentValue.price.value.centAmount * currentValue.quantity
    , 0);
    const shippingCost = shippingInfo && shippingInfo.price ? shippingInfo.price.centAmount : 0;
    const discountTotal = originalTotal - totalPrice.centAmount + shippingCost;
    const orderedItems = lineItems && lineItems.map((lineItem)=>createProductObject(lineItem, lineItem.quantity, formatProductUrl, selfUrl)
    );
    const orderedPGItems = prescriptionGlassesLineItems.map((pgItem)=>createPrescriptionGlassesObject(pgItem, formatProductUrl, selfUrl)
    );
    return {
        '@type': 'Order',
        discount: calcPrice(discountTotal),
        orderNumber: orderNumber || '',
        orderTotal: calcPrice(originalTotal),
        shipmentCost: calcPrice(shippingCost),
        orderedItem: [
            ...orderedItems,
            ...orderedPGItems
        ] || [],
        paymentMethod: getPaymentMethodUrl(paymentMethod || '')
    };
};
/**
 * Sets a new Action Object on the global SD actions storage on window
 */ const pushSdAction = (action)=>{
    if (typeof window === 'undefined') {
        return;
    }
    if (!window.grandvision || !Array.isArray(window.grandvision.actions)) {
        window.grandvision = {
            actions: []
        };
    }
    window.grandvision.actions.push(action);
    if (process.env.NODE_ENV !== 'production') {
        console.log('New Structured Data Action pushed to window.grandvision.actions: ', action);
    }
};
const getFacetLabel = (key, facetLabels)=>{
    const labelObject = facetLabels.find((facetLabel)=>facetLabel.value === key
    );
    return labelObject ? labelObject.label : '';
};
const FACET_BLACKLIST = [
    'variants',
    'attributes',
    'label',
    'centAmount'
];
const getSanitizedFacetName = (facet, locale)=>{
    return facet.split('.').filter((key)=>!(key === locale || FACET_BLACKLIST.includes(key))
    ).join('');
};
var ref;
const getIncludedVatAmount = (product)=>{
    var ref7, ref5, ref6;
    return (ref6 = ((ref = product === null || product === void 0 ? void 0 : (ref7 = product.taxCategory) === null || ref7 === void 0 ? void 0 : (ref5 = ref7.obj) === null || ref5 === void 0 ? void 0 : ref5.rates) !== null && ref !== void 0 ? ref : []).find((rate)=>rate.includedInPrice
    )) === null || ref6 === void 0 ? void 0 : ref6.amount;
};
const createPriceSpecification = (specification, sku, isCentAmount = true)=>{
    if (!specification) {
        return null;
    }
    if (!specification.price) {
        console.warn(`Variant with SKU ${sku} doesn't have any pricing.`);
        return null;
    }
    const { price , vatRate , currency , validFrom , validThrough  } = specification;
    if (typeof vatRate === 'undefined') {
        console.warn(`No VAT amount found for variant with SKU ${sku}.`);
        return null;
    }
    if (vatRate === 0) {
        console.warn(`VAT rate is 0 on variant with SKU ${sku}.`);
    }
    const { priceExVat , vatAmount  } = priceVatCalc(price, vatRate);
    if (!priceExVat || !vatAmount) {
        console.warn(`Missing \'priceExVat\' or \'vatAmount\'`);
        return null;
    }
    return {
        '@type': 'CompoundPriceSpecification',
        name: 'Price',
        price: calcPrice(price, isCentAmount),
        priceCurrency: currency,
        valueAddedTaxIncluded: true,
        validFrom,
        validThrough,
        priceComponent: [
            {
                '@type': 'UnitPriceSpecification',
                name: 'Price excluding VAT',
                price: calcPrice(priceExVat, isCentAmount),
                valueAddedTaxIncluded: false
            },
            {
                '@type': 'UnitPriceSpecification',
                name: 'VAT',
                price: calcPrice(vatAmount, isCentAmount),
                valueAddedTaxIncluded: true
            }, 
        ]
    };
};

export { calcPrice, createOfferObject, createOrderObject, createPrescriptionGlassesObject, createPrescriptionGlassesOffer, createPrescriptionGlassesPrice, createPriceSpecification, createProductObject, getFacetLabel, getIncludedVatAmount, getPaymentMethodUrl, getSanitizedFacetName, getUrlInfo, priceVatCalc, pushSdAction };
//# sourceMappingURL=helpers.js.map
